// src/components/common/CurrentLocationDetector.js
//
// "Detect My Current Location" — replaces the old direct fetch() call to
// the Geocoding REST endpoint (maps.googleapis.com/maps/api/geocode/json),
// which fails with "API keys with referer restrictions cannot be used with
// this API." for browser (HTTP-referrer-restricted) keys. Referrer-restricted
// keys ARE allowed to use the Maps JavaScript API's google.maps.Geocoder
// from inside the browser, so reverse-geocoding now goes through that
// instead — same key, same restrictions, no REST call.
//
// Usage: <CurrentLocationDetector onDetected={setCurrentLocationAddr}
//           buttonStyle={S.detectBtn} errorStyle={S.locationErrorText} />
// onDetected receives the same address shape the old REST-based handler
// produced: { id, label, line1, area, city, state, pincode,
//             formatted_address, lat, lng }.

import React, { useState } from "react";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import CONFIG from "../../config";

const GMAPS_KEY = CONFIG?.GOOGLE_MAPS_API_KEY || "";

const addressFromGeocoderResult = (result, latitude, longitude) => {
  const comp = (type) => result.address_components?.find(c => c.types.includes(type))?.long_name || "";
  return {
    id:       "current-location",
    label:    "Current Location",
    line1:    comp("premise") || comp("sublocality_level_2") || comp("route") || "",
    area:     comp("sublocality_level_1") || comp("sublocality") || comp("neighborhood") || "",
    city:     comp("locality") || comp("administrative_area_level_2") || "",
    state:    comp("administrative_area_level_1") || "",
    pincode:  comp("postal_code") || "",
    formatted_address: result.formatted_address || "",
    lat: latitude, lng: longitude,
    // [lat, lng] array form — sent alongside the scalar lat/lng fields so
    // the backend can store it under delivery_address.geo_location.
    geo_location: [latitude, longitude],
  };
};

// Rendered inside <APIProvider> so useMapsLibrary can reach the Maps JS
// script's loading state via context.
const Detector = ({ onDetected, buttonStyle, errorStyle, idleLabel = "📍 Detect My Current Location" }) => {
  const geocodingLib = useMapsLibrary("geocoding");
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState("");

  const handleClick = () => {
    setError("");
    if (!navigator.geolocation) {
      setError("Location access isn't supported by your browser.");
      return;
    }
    if (!geocodingLib) {
      setError("Google Maps is still loading — please try again in a moment.");
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const geocoder = new geocodingLib.Geocoder();
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          setDetecting(false);
          if (status !== "OK" || !results?.[0]) {
            setError(status === "ZERO_RESULTS"
              ? "No address found for this location."
              : `Couldn't resolve your address (${status}).`);
            return;
          }
          onDetected(addressFromGeocoderResult(results[0], latitude, longitude));
        });
      },
      (geoErr) => {
        setDetecting(false);
        // GeolocationPositionError codes: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        if (geoErr.code === 1)      setError("Location permission denied. Please allow location access and try again.");
        else if (geoErr.code === 2) setError("Your location is unavailable right now. Please try again.");
        else if (geoErr.code === 3) setError("Location request timed out. Please try again.");
        else                        setError("Couldn't access your location. Please allow location permission.");
      },
      { timeout: 8000, enableHighAccuracy: true }
    );
  };

  return (
    <>
      <button type="button" style={buttonStyle} onClick={handleClick} disabled={detecting}>
        {detecting ? "Detecting your location…" : idleLabel}
      </button>
      {error && <div style={errorStyle}>{error}</div>}
    </>
  );
};

const CurrentLocationDetector = (props) => {
  if (!GMAPS_KEY) return null; // caller shows its own Testing Mode UI instead
  return (
    <APIProvider apiKey={GMAPS_KEY}>
      <Detector {...props} />
    </APIProvider>
  );
};

export default CurrentLocationDetector;
